/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
    Attachment,
    AttachmentIO,
    Category,
    CategoryCollectionIO,
    FeatureCollection,
    fetchIO,
    fetchPaginatedIO,
    fetchWithoutValidationIO,
    IAliasCollection,
    IAliasCollectionIO,
    IMapBaseLayer,
    IMapBaseLayerIO,
    IMapInfo,
    IMapInfoZ,
    Inspire,
    InspireIO,
    IUser,
    IUserIO,
    MapLinkListIO,
    GeometryObject,
    postUnrelatedIO,
    FeatureCollectionIO,
    ILayerInfo,
    ILayerInfoIO,
    IBaselayerCategory,
    IBaselayerCategoryIO,
    postWithoutValidationIO,
    StreamingRequestFilter,
} from 'sdi/source';

import * as io from 'io-ts';
import * as z from 'zod';

interface HarvestRequest {
    metadata: string;
    geometry: GeometryObject;
}

export const fetchLayer = (url: string): Promise<FeatureCollection> =>
    fetchWithoutValidationIO(url);
export const fetchBaseLayer = (url: string): Promise<IMapBaseLayer> =>
    fetchIO(IMapBaseLayerIO, url);
export const fetchBaseLayerAll = (url: string): Promise<IMapBaseLayer[]> =>
    fetchIO(io.array(IMapBaseLayerIO), url);

export const fetchBaseLayerCategory = (
    url: string,
): Promise<IBaselayerCategory[]> =>
    fetchIO(io.array(IBaselayerCategoryIO), url);
export const fetchAllMaps = (url: string): Promise<IMapInfo[]> =>
    fetchIO(z.array(IMapInfoZ), url);
export const fetchMap = (url: string): Promise<IMapInfo> =>
    fetchIO(IMapInfoZ, url);
export const fetchAlias = (url: string): Promise<IAliasCollection> =>
    fetchIO(IAliasCollectionIO, url);
export const fetchUser = (url: string): Promise<IUser> => fetchIO(IUserIO, url);
export const fetchUsers = (url: string): Promise<IUser[]> =>
    fetchIO(io.array(IUserIO), url);
export const fetchLayers = (url: string): Promise<ILayerInfo[]> =>
    fetchIO(io.array(ILayerInfoIO), url);

export const fetchDatasetMetadata = (url: string): Promise<Inspire> =>
    fetchIO(InspireIO, url);
export const fetchAllDatasetMetadata = (url: string) =>
    fetchPaginatedIO(InspireIO, url);

export const fetchCategories = (url: string): Promise<Category[]> =>
    fetchIO(CategoryCollectionIO, url);
export const fetchAttachment = (url: string): Promise<Attachment> =>
    fetchIO(AttachmentIO, url);

export const fetchLinks = (url: string) => fetchIO(MapLinkListIO, url);

export const postHarvestRequest = (url: string, request: HarvestRequest) =>
    postUnrelatedIO(FeatureCollectionIO, url, request);

export const fetchFilteredLayer = (
    url: string,
    filters: StreamingRequestFilter[],
): Promise<FeatureCollection> => postWithoutValidationIO(url, { filters });
